import { $lang, useTranslation } from '@msdk/common'
import {
  ActionMenuBehaviour,
  EntityContext,
  EntityModel,
  FormFieldDefinition,
  FormHeaderDefinition,
  FormModel,
} from '@msdk/definitions'
import { IconSelector, styled, THEME_CONST, Typography } from '@msdk/ui'
import React from 'react'
import { useRecoilValue } from 'recoil'

import { useEntityFromCollection } from '../../state'
import { getFieldDef } from '../../utils'
import { HeaderIcon } from './HeaderIcon'

const Container = styled('div')`
  display: flex;
  flex-direction: row;
  gap: 16px;
  padding: ${THEME_CONST.defaultPadding}px;
`

const NameContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const SubItemContainer = styled('div')`
  display: flex;
  flex-direction: row;
  gap: 20px;
`

const SubItem = styled('div')`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
  white-space: pre-wrap;

  svg {
    font-size: 16px;
  }
`

const TitleContainer = styled('div')`
  display: flex;
  flex-direction: row;
  padding: 2px 0 0;
  white-space: pre-wrap;
`

interface Props {
  header: FormHeaderDefinition
  entity: EntityContext
  entityModel: EntityModel
  formModel: FormModel
  actions?: React.ReactElement
}

export function FrameworkHeader(props: Props) {
  const entity = useEntityFromCollection(props.entity?.id as string)
  const t = useTranslation()
  const lang = useRecoilValue($lang)

  const getMatches = () => {
    const regex = /(\B|[^{]){{[^{}]*}}(?!})/g
    const matches = Array.from(props.header.top.matchAll(regex))
    return matches
  }

  const getTitle = () => {
    const matches = getMatches()
    let resString = props.header.top
    matches.forEach((m) => {
      const placeholder = m[0].trimStart()
      const fieldName = placeholder.replace('{{', '').replace('}}', '')
      const field = entity?.fields[fieldName]
      const value = field ? field.toStringForView(lang) : ''
      resString = resString.replace(placeholder, value)
    })
    return resString
  }

  const getSubItem = (i: FormFieldDefinition) => {
    const field = entity?.fields[i.name]
    const value = field ? field.toStringForView(lang) : ''

    switch (i.type) {
      case 'Field': {
        const fDef = getFieldDef(props.entityModel, props.formModel, i.name)
        const desc = i.overrideDescription ? i.overrideDescription : fDef.description
        const idml = i.overrideIDML ? i.overrideIDML : fDef.idml
        return (
          <SubItem key={i.name}>
            <Typography variant="body2">{`${t(idml, desc)}: ${value}`}</Typography>
          </SubItem>
        )
      }
      case 'FieldValue': {
        return (
          <SubItem key={i.name}>
            {i.icon && <IconSelector iconName={i.icon} />}
            <Typography variant="body2">{value}</Typography>
          </SubItem>
        )
      }
      default:
        throw new Error(`[Form Header] - Unsupported SubItem type: ${i.type}`)
    }
  }

  const getInitials = () => {
    const matches = getMatches()
    const result: string[] = []
    matches.forEach((m) => {
      const placeholder = m[0].trimStart()
      const fieldName = placeholder.replace('{{', '').replace('}}', '')
      const field = entity?.fields[fieldName]
      const value = field ? field.toStringForView(lang)[0] : ''
      result.push(value)
    })
    return result
  }

  return (
    <Container>
      <HeaderIcon icon={props.header.icon} intials={getInitials()} />
      <NameContainer>
        <TitleContainer>
          <Typography variant="h3">{getTitle()}</Typography>
          {props.formModel.actionMenuBehaviour === ActionMenuBehaviour.Default && props.actions}
        </TitleContainer>
        <SubItemContainer>{(props.header.subItem ?? []).map((i) => getSubItem(i))}</SubItemContainer>
      </NameContainer>
    </Container>
  )
}
